import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Home,
  LayoutDashboard,
  Layers,
  MessageCircle,
  Settings,
  User,
} from 'lucide-react';
import { AppPhase } from '../../types';
import {
  isCommunicatePhase,
  isGradeWorkspacePhase,
  isHomeHubPhase,
  isOptionsPhase,
  isPlanPhase,
  isSchedulePhase,
  isTeachPhase,
} from '../../lib/appShellPhaseGroups';
import { GRADING_ONLY_MODE } from '../../lib/gradingOnlyMode';

const SIDEBAR_KEY = 'dg_sidebar_collapsed_v1';

export type DesktopSidebarProps = {
  phase: AppPhase;
  isSignedIn: boolean;
  isOnline: boolean;
  setPhase: (p: AppPhase) => void;
  bumpNavUsage: (key: 'plan' | 'grade' | 'teach' | 'communicate') => void;
  recordShortcutUse: (
    key: 'plan' | 'grade' | 'teach' | 'schedule' | 'communicate' | 'options'
  ) => void;
  /** When true, hide non-grade nav items (grading-only product). */
  gradingOnlyMode?: boolean;
  onOpenGradingSettings?: () => void;
};

type NavItem = {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  active: boolean;
  disabled?: boolean;
  disabledTitleKey?: string;
  onSelect: () => void;
  /** V2 active rail — 4px left bar (UI_TEACHER_V2 §3) */
  activeBarClass: string;
  accentClass: string;
};

export function DesktopSidebar({
  phase,
  isSignedIn,
  isOnline,
  setPhase,
  bumpNavUsage,
  recordShortcutUse,
  gradingOnlyMode = GRADING_ONLY_MODE,
  onOpenGradingSettings,
}: DesktopSidebarProps): React.ReactElement {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
    const w = collapsed ? 72 : 256;
    document.documentElement.style.setProperty('--dg-sidebar-w', `${w}px`);
  }, [collapsed]);

  const go = useCallback(
    (p: AppPhase) => {
      setPhase(p);
    },
    [setPhase]
  );

  /** Order: Home, Plan, Grade, Teach, Schedule, Communicate, Options (UI_TEACHER_V2 §3) */
  const allItems: NavItem[] = [
    {
      id: 'home',
      labelKey: 'nav.home',
      icon: Home,
      active: isHomeHubPhase(phase),
      disabled: false,
      onSelect: () => go(AppPhase.AUTHENTICATION),
      activeBarClass: 'border-l-blue-500 dark:border-l-blue-400',
      accentClass: 'text-dg-accent-home dark:text-blue-400',
    },
    {
      id: 'plan',
      labelKey: 'nav.plan',
      icon: BookOpen,
      active: isPlanPhase(phase),
      disabled: !isSignedIn,
      disabledTitleKey: undefined,
      onSelect: () => {
        if (!isSignedIn) return;
        recordShortcutUse('plan');
        go(AppPhase.PLAN);
      },
      activeBarClass: 'border-l-dg-accent-plan dark:border-l-violet-400',
      accentClass: 'text-dg-accent-plan dark:text-violet-400',
    },
    {
      id: 'grade',
      labelKey: 'nav.grade',
      icon: LayoutDashboard,
      active: isGradeWorkspacePhase(phase),
      disabled: !isSignedIn,
      onSelect: () => {
        if (!isSignedIn) return;
        go(AppPhase.DASHBOARD);
      },
      activeBarClass: 'border-l-dg-accent-grade dark:border-l-amber-400',
      accentClass: 'text-dg-accent-grade dark:text-amber-400',
    },
    {
      id: 'teach',
      labelKey: 'nav.teach',
      icon: Layers,
      active: isTeachPhase(phase),
      disabled: !isSignedIn || !isOnline,
      disabledTitleKey: !isSignedIn ? undefined : !isOnline ? 'nav.teachOfflineHint' : undefined,
      onSelect: () => {
        if (!isSignedIn || !isOnline) return;
        bumpNavUsage('teach');
        go(AppPhase.CLASSROOM);
      },
      activeBarClass: 'border-l-dg-accent-teach dark:border-l-teal-400',
      accentClass: 'text-dg-accent-teach dark:text-teal-400',
    },
    {
      id: 'schedule',
      labelKey: 'nav.schedule',
      icon: Calendar,
      active: isSchedulePhase(phase),
      disabled: !isSignedIn,
      onSelect: () => {
        if (!isSignedIn) return;
        go(AppPhase.SCHEDULE);
      },
      activeBarClass: 'border-l-dg-accent-schedule dark:border-l-emerald-400',
      accentClass: 'text-dg-accent-schedule dark:text-emerald-400',
    },
    {
      id: 'communicate',
      labelKey: 'nav.communicate',
      icon: MessageCircle,
      active: isCommunicatePhase(phase),
      disabled: !isSignedIn || !isOnline,
      disabledTitleKey: !isSignedIn
        ? 'nav.communicateSignIn'
        : !isOnline
          ? 'nav.communicateNeedsInternet'
          : undefined,
      onSelect: () => {
        if (!isSignedIn || !isOnline) return;
        bumpNavUsage('communicate');
        go(AppPhase.RECORDS);
      },
      activeBarClass: 'border-l-dg-accent-communicate dark:border-l-rose-400',
      accentClass: 'text-dg-accent-communicate dark:text-rose-400',
    },
    {
      id: 'options',
      labelKey: 'nav.options',
      icon: Settings,
      active: isOptionsPhase(phase),
      disabled: !isSignedIn,
      onSelect: () => {
        if (!isSignedIn) return;
        go(AppPhase.OPTIONS);
      },
      activeBarClass: 'border-l-dg-accent-options dark:border-l-slate-400',
      accentClass: 'text-dg-accent-options dark:text-slate-300',
    },
  ];

  const gradingOnlyNavIds = new Set(['grade']);
  const items = gradingOnlyMode
    ? allItems.filter((item) => gradingOnlyNavIds.has(item.id))
    : allItems;

  return (
    <aside
      className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-sidebar border-r border-dg-border bg-dg-bg-surface backdrop-blur-sm transition-[width] duration-200 ease-out dark:border-slate-800 dark:bg-slate-950 ${
        collapsed ? 'w-[72px]' : 'w-64'
      }`}
      aria-label={t('shell.sidebarAria')}
    >
      <div className="shrink-0 flex items-center gap-2 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 border-b border-dg-border-subtle dark:border-slate-800">
        <img
          src="/donegrading-logo.png"
          alt="DoneGrading"
          decoding="async"
          className="h-9 w-9 shrink-0 rounded-full object-cover"
        />
        {!collapsed ? (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate leading-tight">
              DoneGrading
            </p>
            <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 truncate">
              {gradingOnlyMode ? t('gradingOnly.shell.brandSubtitle') : t('shell.workspace')}
            </p>
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="shrink-0 rounded-dg-control border border-dg-border p-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" aria-hidden />
          ) : (
            <ChevronLeft className="w-4 h-4" aria-hidden />
          )}
        </button>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden py-2 px-2">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.active;
          const disabled = item.disabled;
          const title =
            item.id === 'plan' && isSignedIn && !isOnline
              ? t('nav.planOfflineHint')
              : disabled && item.disabledTitleKey
                ? t(item.disabledTitleKey)
                : undefined;
          return (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              title={title}
              onClick={item.onSelect}
              aria-current={active ? 'page' : undefined}
              className={`relative flex min-h-[var(--dg-touch-min)] items-center gap-3 rounded-dg-control py-2.5 pl-3 pr-3 text-left transition-colors ${
                active
                  ? `dg-nav-rail-active ${item.activeBarClass}`
                  : disabled
                    ? 'cursor-not-allowed border-l-4 border-l-transparent text-dg-fg-disabled opacity-60 dark:text-slate-600'
                    : 'border-l-4 border-l-transparent text-dg-fg-secondary hover:bg-dg-interaction-hover dark:text-slate-200 dark:hover:bg-slate-800/80'
              }`}
            >
              <Icon className={`h-5 w-5 shrink-0 ${active ? item.accentClass : ''}`} aria-hidden />
              {!collapsed ? (
                <span
                  className={`truncate text-dg-body ${active ? 'font-semibold text-dg-fg-primary dark:text-slate-50' : ''}`}
                >
                  {t(item.labelKey)}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className="flex shrink-0 flex-col gap-1 border-t border-dg-border-subtle p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-slate-800">
        <button
          type="button"
          disabled={!isSignedIn}
          onClick={() => isSignedIn && go(AppPhase.OPTIONS)}
          className="flex min-h-[44px] items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover disabled:pointer-events-none disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800/60"
          aria-label={t('shell.help')}
        >
          <HelpCircle className="w-5 h-5 shrink-0" aria-hidden />
          {!collapsed ? <span className="text-dg-body font-medium">{t('shell.help')}</span> : null}
        </button>
        <button
          type="button"
          disabled
          className="flex min-h-[44px] cursor-not-allowed items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-disabled opacity-70 dark:text-slate-600"
          title={t('shell.notificationsSoon')}
          aria-label={t('shell.notificationsSoon')}
        >
          <Bell className="h-5 w-5 shrink-0" aria-hidden />
          {!collapsed ? (
            <span className="text-dg-body font-medium">{t('shell.notifications')}</span>
          ) : null}
        </button>
        <button
          type="button"
          disabled={!isSignedIn}
          onClick={() => {
            if (!isSignedIn) return;
            if (gradingOnlyMode && onOpenGradingSettings) {
              onOpenGradingSettings();
              return;
            }
            go(AppPhase.OPTIONS);
          }}
          className="flex min-h-[44px] items-center gap-3 rounded-dg-control px-3 py-2 text-dg-fg-secondary transition-colors hover:bg-dg-interaction-hover disabled:pointer-events-none disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800/60"
          aria-label={t('shell.profile')}
        >
          <User className="h-5 w-5 shrink-0" aria-hidden />
          {!collapsed ? (
            <span className="text-dg-body font-medium">{t('shell.profile')}</span>
          ) : null}
        </button>
      </div>
    </aside>
  );
}
